/**
 * Configuration values for loss estimation calculations
 * These represent the base parameters used across all calculations
 */
export interface ConfigValues {
  discount: number; // descuento: Discount percentage applied to parts
  vat: number; // iva: Value Added Tax percentage
  bodyworkRate: number; // tarifaCarroceria: Hourly rate for bodywork
  paintRate: number; // tarifaPintura: Hourly rate for painting
  mechanicalRate: number; // tarifaMecanica: Hourly rate for mechanical work
  underinsurance: number; // infraseguro: Underinsurance amount (también usado como monto asegurado)
  depreciation: number; // depreciaciones: Depreciation amount
  deductible: number; // deducible: Deductible amount
  isStructuralLoss: boolean; // Is this a structural loss? (default: false)
  isFinancialLoss: boolean; // Is this a financial loss? (default: false)
}

/**
 * Conditional variables for parts calculations
 * These variables determine the discount percentage based on workshop and company
 */
export interface PartsVariables {
  workshop: 'Purdy Auto' | 'Otros Talleres';
  company: 'Purdy Motor' | 'Automotriz';
  brand: string; // Selected brand from the company's brand list
}

/**
 * Conditional variables for labor calculations
 * These variables determine the labor cost based on workshop and type
 */
export interface LaborVariables {
  workshop: 'Purdy Auto' | 'Otros Talleres';
  type: 'Interno' | 'Externo';
}

/**
 * Brand options for each company type
 */
export const BRAND_OPTIONS = {
  'Purdy Motor': ['Toyota', 'Lexus', 'Subaru', 'Hino', 'Daihatsu'],
  Automotriz: ['Ford', 'Volkswagen'],
} as const;

/**
 * Discount matrix based on workshop and company combination
 */
export const DISCOUNT_MATRIX = {
  'Purdy Auto': {
    'Purdy Motor': 15,
    Automotriz: 10,
  },
  'Otros Talleres': {
    'Purdy Motor': 20,
    Automotriz: 15,
  },
} as const;

/**
 * Labor cost matrix based on workshop and type combination
 */
export const LABOR_COST_MATRIX = {
  'Purdy Auto': {
    Interno: 16800,
  },
  'Otros Talleres': {
    Externo: 13000,
  },
} as const;

/**
 * Input values for loss calculation
 * These represent the specific claim details to be calculated
 */
export interface CalculationValues {
  netParts: number; // netoRepuestos: Net value of parts before discount
  bodyworkHours: number; // horasCarroceria: Hours of bodywork required
  paintHours: number; // horasPintura: Hours of painting required
  mechanicalHours: number; // horasMecanica: Hours of mechanical work required
  hiddenDamagePartsNoVAT: number; // danoOcultoRESinIVA: Hidden damage to parts without VAT
  hiddenDamageLaborNoVAT: number; // danoOcultoMOSinIVA: Hidden damage to labor without VAT
  additionalLabor: number; // adicionalesMO: Additional labor costs
  additionalParts: number; // adicionalesRE: Additional parts costs
  subletAmount: number; // montoSublet: Sublet amount to be added to final calculation
  // UT Audatex conversions (1 hour = 10 UT)
  bodyworkUT: number; // UT for bodywork hours
  paintUT: number; // UT for paint hours
  mechanicalUT: number; // UT for mechanical hours
}

/**
 * Results of loss estimation calculations
 * These represent the calculated amounts for insurance claim processing
 */
export interface CalculationResults {
  // Parts calculations
  netTotalParts: number; // netoTotalRepuestos: Parts after discount
  preliminaryPartsLoss: number; // montoPreliminarPerdidaRepuestos: Parts loss with VAT
  hiddenDamagePartsWithVAT: number; // danoOcultoREConIVA: Hidden damage parts with VAT
  totalPartsLoss: number; // montoTotalPerdidaRepuestos: Total parts loss

  // Labor calculations
  preliminaryLaborNoVAT: number; // montoPreliminarMOSinIVA: Labor cost by hours and rates without VAT
  preliminaryLabor: number; // montoPreliminarMO: Labor cost by hours and rates with VAT
  hiddenDamageLaborWithVAT: number; // danoOcultoMOConIVA: Hidden damage labor with VAT
  totalLaborLoss: number; // montoTotalPerdidaMO: Total labor loss

  // Final calculations
  totalLossAmount: number; // montoTotalPerdida: Total loss amount
  totalRepairAmount: number; // montoTotalReparacion: Total repair amount
  initialIndemnityAmount: number; // montoIndemnizarInicial: Initial indemnity before adjustments
  finalIndemnityAmount: number; // totalMontoIndemnizar: Final amount to be paid
}

export interface InitialConfigFormProps {
  poNumber: string;
  claimUId: string;
  onNext: (values: ConfigValues) => void;
  initialValues: ConfigValues;
  isJsonLoaded?: boolean;
}

export interface CalculationFormProps {
  poNumber: string;
  claimUId: string;
  configValues: ConfigValues;
  partsVariables?: PartsVariables;
  laborVariables?: LaborVariables;
  initialCalculationValues?: CalculationValues;
  siseSiniestro?: string;
  onRefresh?: () => void;
  estimations?: any[];
  isJsonLoaded?: boolean;
}

/**
 * Extended calculation values that include conditional variables
 */
export interface ExtendedCalculationValues extends CalculationValues {
  partsVariables: PartsVariables;
  laborVariables: LaborVariables;
}

/**
 * Extended calculation results that include conditional calculations
 */
export interface ExtendedCalculationResults extends CalculationResults {
  conditionalDiscount: number; // Discount calculated from variables
  conditionalLaborCost: number; // Labor cost calculated from variables
  appliedVariables: {
    parts: PartsVariables;
    labor: LaborVariables;
  };
}
